import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getConsent, saveConsent, applyConsent } from "@/lib/consent";

export default function CookieBanner() {
    const [visible, setVisible] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [analytics, setAnalytics] = useState(false);
    const [ads, setAds] = useState(false);

    // On mount: apply stored consent, or show banner if none.
    useEffect(() => {
        const stored = getConsent();
        if (stored) {
            applyConsent(stored);
        } else {
            setVisible(true);
        }
        const openSettings = () => {
            const current = getConsent();
            setAnalytics(current ? current.analytics : false);
            setAds(current ? current.ads : false);
            setShowSettings(true);
            setVisible(true);
        };
        window.addEventListener("dep:open-cookie-settings", openSettings);
        return () => window.removeEventListener("dep:open-cookie-settings", openSettings);
    }, []);

    const persist = (choice) => {
        saveConsent(choice);
        applyConsent(choice);
        setVisible(false);
        setShowSettings(false);
    };

    const acceptAll = () => persist({ analytics: true, ads: true });
    const rejectAll = () => persist({ analytics: false, ads: false });
    const savePrefs = () => persist({ analytics, ads });

    if (!visible) return null;

    return (
        <div
            data-testid="cookie-banner"
            className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6"
            role="dialog"
            aria-live="polite"
            aria-label="Aviso de cookies"
        >
            <div
                className="mx-auto max-w-3xl border border-gold/30 backdrop-blur-xl p-5 sm:p-6 shadow-2xl"
                style={{ background: "rgba(14, 22, 38, 0.94)" }}
            >
                <div className="space-y-2">
                    <p className="font-serif text-lg text-white">Tu privacidad</p>
                    <p className="text-sm text-zinc-400 font-light leading-relaxed">
                        Usamos cookies técnicas necesarias para el funcionamiento del sitio
                        y, solo si lo aceptas, cookies analíticas y publicitarias (incluido
                        el píxel de Meta). Puedes aceptar, rechazar o configurar. Consulta
                        nuestra{" "}
                        <Link
                            to="/politica-de-cookies"
                            data-testid="cookie-banner-policy-link"
                            className="text-gold underline underline-offset-2 hover:text-beam"
                        >
                            Política de Cookies
                        </Link>
                        .
                    </p>
                </div>

                {showSettings && (
                    <div
                        data-testid="cookie-settings-panel"
                        className="mt-5 space-y-3 border-t border-white/10 pt-4"
                    >
                        <label className="flex items-start gap-3 text-sm text-zinc-300">
                            <input
                                type="checkbox"
                                checked
                                disabled
                                data-testid="cookie-necessary-toggle"
                                className="mt-1 accent-[#F4C9B5]"
                            />
                            <span>
                                <span className="text-zinc-100">Técnicas y necesarias</span>{" "}
                                — imprescindibles, siempre activas.
                            </span>
                        </label>
                        <label className="flex items-start gap-3 text-sm text-zinc-300 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={analytics}
                                onChange={(e) => setAnalytics(e.target.checked)}
                                data-testid="cookie-analytics-toggle"
                                className="mt-1 accent-[#F4C9B5]"
                            />
                            <span>
                                <span className="text-zinc-100">Analíticas</span> — medir
                                visitas y mejorar la web.
                            </span>
                        </label>
                        <label className="flex items-start gap-3 text-sm text-zinc-300 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={ads}
                                onChange={(e) => setAds(e.target.checked)}
                                data-testid="cookie-ads-toggle"
                                className="mt-1 accent-[#F4C9B5]"
                            />
                            <span>
                                <span className="text-zinc-100">Publicitarias</span> — píxel
                                de Meta para medir anuncios.
                            </span>
                        </label>
                    </div>
                )}

                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                    {!showSettings ? (
                        <>
                            <button
                                onClick={acceptAll}
                                data-testid="cookie-accept-button"
                                className="btn-gold justify-center flex-1"
                            >
                                Aceptar
                            </button>
                            <button
                                onClick={rejectAll}
                                data-testid="cookie-reject-button"
                                className="btn-gold justify-center flex-1"
                            >
                                Rechazar
                            </button>
                            <button
                                onClick={() => setShowSettings(true)}
                                data-testid="cookie-configure-button"
                                className="btn-outline-gold justify-center flex-1"
                            >
                                Configurar
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={savePrefs}
                                data-testid="cookie-save-button"
                                className="btn-gold justify-center flex-1"
                            >
                                Guardar preferencias
                            </button>
                            <button
                                onClick={rejectAll}
                                data-testid="cookie-reject-button"
                                className="btn-outline-gold justify-center flex-1"
                            >
                                Rechazar todo
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
